// src/interfaces/user/DraftsList.js
import React, { useEffect, useState } from 'react';
import { useQuotes } from '../../context/QuoteContext';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/common/Modal.js';
import QuoteReview from './QuoteReview.js';
import ManagerQuoteReview from '../../components/manager/ManagerQuoteReview.js';
import { convertDraftToQuoteItems } from '../../utils/quoteItemBuilder';

const DraftsList = ({ setActivePage }) => { // Accept setActivePage as a prop
  const {
    drafts,
    draftsTotal,
    draftsHasMore,
    draftsLoading: isLoading,
    error,
    fetchDraftsWithContext,
    loadMoreDrafts,
    deleteDraft,
    prepareQuoteForEditing,
    prepareDraftForQuoteReview,
    addQuote, // For converting draft to quote
    startEditingDraft,
    currentQuote,
    addProductToQuote,
    clearCurrentQuote,
    removeProductFromQuote,
    markQuoteAsConvertedFromDraft
  } = useQuotes();

  const { isAuthenticated, userRole, hasRole } = useAuth();

  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedDraft, setSelectedDraft] = useState(null);

  // Quote review modal state
  const [showQuoteReview, setShowQuoteReview] = useState(false);
  const [draftBeingConverted, setDraftBeingConverted] = useState(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchDraftsWithContext('management', { search: searchTerm, limit: 50, offset: 0 });
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm, fetchDraftsWithContext]);

  // Handle clearing current quote
  const handleClearCurrentQuote = () => {
    if (window.confirm('Clear all items from current quote?')) {
      clearCurrentQuote();
    }
  };

  // Handle opening quote review from sidebar
  const handleReviewCurrentQuote = () => {
    // Set up quote review with current quote data
    setShowQuoteReview(true);
  };

  // Handle removing individual item from current quote
  const handleRemoveFromQuote = (itemId) => {
    removeProductFromQuote(itemId);
  };

  // --- Handler Functions ---
  const handleEditDraft = async (draft) => {
    // Load the draft into the calculator for editing
    await prepareQuoteForEditing(draft);
    // Mark this draft as being edited so Save updates instead of creating a new one
    startEditingDraft(draft.id);

    if (typeof setActivePage === 'function') {
      setActivePage('calculator'); // Navigate to the calculator page/view
    } else {
      console.warn('DraftsList: setActivePage prop is not a function. Cannot navigate.');
    }
  };

const handleSaveAsQuote = async (draft) => {
  try {
    // Load the draft into the current quote for review (without navigation)
    // prepareDraftForQuoteReview is now async, so we await it
    await prepareDraftForQuoteReview(draft);

    // Store the draft being converted for later reference
    setDraftBeingConverted(draft);

    // Open the quote review modal
    setShowQuoteReview(true);
  } catch (error) {
    console.error('Error preparing draft for quote review:', error);
    alert(`Error: ${error.message}`);
  }
};

  // Handle adding draft directly to existing quote
  // Now uses shared conversion utility for full schema compatibility
  const handleAddToQuote = async (draft) => {
    try {
      console.log('Adding draft to quote:', draft);

      // Use the shared conversion utility to convert draft to quote items
      // Note: We pass empty reference data object - the utility will handle missing data gracefully
      // In a future optimization, we could fetch and cache reference data in DraftsList
      const items = convertDraftToQuoteItems(draft, {});

      // Add each item to the current quote
      items.forEach(item => {
        addProductToQuote(item);
      });

      // Mark the current quote as being converted from a draft
      markQuoteAsConvertedFromDraft();

      // Show success feedback
      const itemCount = items.length;
      const message = itemCount === 1
        ? `"${draft.draftName || 'Draft'}" added to quote successfully!`
        : `"${draft.draftName || 'Draft'}" (${itemCount} items) added to quote successfully!`;

      alert(message);
      console.log(`Draft converted to ${itemCount} quote item(s):`, items);
    } catch (error) {
      console.error('Error adding draft to quote:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Handle quote review modal close
  const handleQuoteReviewClose = async (closeReason = 'cancelled') => {
    console.log('Quote review closed with reason:', closeReason);
    setShowQuoteReview(false);

    // Only delete the draft if a quote was actually successfully submitted/saved
    const shouldDeleteDraft = [
      'quote_submitted',                    // User submitted quote
      'quote_saved',                       // Manager saved quote
      'order_submitted_to_printlogic',     // Manager submitted to Printlogic
      'quote_saved_printlogic_failed'      // Manager saved quote (even if Printlogic failed)
    ].includes(closeReason);

    if (shouldDeleteDraft && draftBeingConverted) {
      try {
        await deleteDraft(draftBeingConverted.id);
        console.log('Draft deleted after successful quote submission');
      } catch (error) {
        console.error('Error deleting draft after quote submission:', error);
      }
    } else {
      console.log('Draft preserved - quote review was cancelled or not completed');
    }

    // Always clear the current quote and reset state
    setDraftBeingConverted(null);
  };

  const handleDeleteDraft = async (draftId) => {
    if (window.confirm('Are you sure you want to delete this draft? This action cannot be undone.')) {
      const success = await deleteDraft(draftId);
      if (success) {
        setSelectedDraft(null); // Close modal if open
      } else {
        alert('Failed to delete draft. Please try again.');
      }
    }
  };

  // --- Utility Functions (copied from QuotesList) ---
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    try {
        return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (e) {
        return 'Invalid Date';
    }
  };

  const getStatusBadge = (status) => {
    // For drafts, we'll always show a draft badge
    return 'bg-orange-100 text-orange-800';
  };

  const getQuoteType = (draft) => {
    // FIRST: Check top-level productType and calculatorType (these are authoritative indicators)
    // BACKWARD COMPATIBILITY: Check both calculatorType (old draft format) and productType (new quote format)
    // Also check both hyphen ('small-format') and underscore ('small_format') versions
    if (draft?.calculatorType === 'small-format' ||
        draft?.productType === 'small-format' ||
        draft?.productType === 'small_format') return 'Small Format';
    if (draft?.calculatorType === 'large-format' ||
        draft?.productType === 'large-format' ||
        draft?.productType === 'large_format') return 'Large Format';

    // FALLBACK: Check items array if top-level indicators not found
    const firstItem = draft?.items?.[0];
    if (!firstItem) {
      if (draft?.material) return 'Large Format';
      if (draft?.paperStock) return 'Small Format';
      return 'Unknown';
    }
    // Check productType FIRST (explicit format indicator) before checking other properties
    // This ensures NCR jobs (which have 'material' field for paper names) are correctly identified as Small Format
    if (firstItem.productType === 'small-format' || firstItem.productType === 'small_format') {
        return 'Small Format';
    }
    if (firstItem.productType === 'large-format' || firstItem.productType === 'large_format') {
        return 'Large Format';
    }
    // Fallback: Check other properties if productType not explicitly set (backward compatibility)
    if (firstItem.paperStock) {
        return 'Small Format';
    }
    if (firstItem.material) {
        return 'Large Format';
    }
    return 'Unknown';
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'Small Format': return 'bg-blue-100 text-blue-800';
      case 'Large Format': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const viewDraftDetails = (draft) => {
    setSelectedDraft(draft);
  };

  const closeDraftDetails = () => {
    setSelectedDraft(null);
  };

  const getDraftName = (draft) => {
    const firstItem = draft?.items?.[0];
    return draft.draftName || draft.quoteName || firstItem?.jobName || draft.jobName || 'Untitled Draft';
  };

  const getDraftSummary = (draft) => {
    const draftType = getQuoteType(draft);
    const firstItem = draft?.items?.[0];

    if (draft.items && draft.items.length > 1) {
      return `${draft.items.length} items`;
    }
    if (firstItem) {
      if (draftType === 'Small Format') {
        const stockDisplay = firstItem.paperStockName || firstItem.paperStock || 'N/A';
        return stockDisplay;
      }
      if (draftType === 'Large Format') {
        if (firstItem && firstItem.jobType === 'Boards') {
          const boardMaterial = firstItem.materials?.find(m => m.type === 'board');
          if (boardMaterial?.name) {
            return boardMaterial.name;
          }
          let materialDisplayString = firstItem.materialNames || firstItem.material || '';
          const parts = materialDisplayString.split(',')
            .map(part => part.trim())
            .filter(part => part.toLowerCase() !== 'standard vinyl');
          
          const cleanedDisplayString = parts.join(', ');
          
          return cleanedDisplayString || 'N/A';
        } else {
          const materialDisplay = firstItem.materialNames || firstItem.material || 'N/A';
          return materialDisplay;
        }
      }
    }
    
    // For drafts without items (saved from calculator state)
    if (draftType === 'Small Format') {
      return draft.paperStockName || draft.paperStock || 'In Progress';
    }
    if (draftType === 'Large Format') {
      return draft.materialNames || draft.material || 'In Progress';
    }
    
    return 'Details unavailable';
  };

  const getDraftDimensions = (draft) => {
    const firstItem = draft?.items?.[0];
    if (draft.items && draft.items.length > 1) {
      return '';
    }
    if (firstItem) {
      return firstItem.dimensions || (firstItem.width && firstItem.height ? `${firstItem.width}x${firstItem.height}mm` : '');
    }
    // For drafts without items
    return draft.dimensions || (draft.width && draft.height ? `${draft.width}x${draft.height}mm` : '');
  };

  // Filter and sort drafts
  const filteredDrafts = drafts.filter(draft => {
    const name = getDraftName(draft);
    const summary = getDraftSummary(draft);
    const searchTermLower = searchTerm.toLowerCase();

    return (
      name?.toLowerCase().includes(searchTermLower) ||
      draft.customerName?.toLowerCase().includes(searchTermLower) ||
      summary?.toLowerCase().includes(searchTermLower) ||
      draft.id?.toLowerCase().includes(searchTermLower)
    );
  });

  const sortedDrafts = [...filteredDrafts].sort((a, b) => {
    if (sortBy === 'date') {
      return sortOrder === 'asc'
        ? new Date(a.createdAt) - new Date(b.createdAt)
        : new Date(b.createdAt) - new Date(a.createdAt);
    } else if (sortBy === 'price') {
      const priceA = a.totalPrice || a.price || 0;
      const priceB = b.totalPrice || b.price || 0;
      return sortOrder === 'asc' ? priceA - priceB : priceB - priceA;
    } else if (sortBy === 'name') {
      const nameA = getDraftName(a);
      const nameB = getDraftName(b);
      return sortOrder === 'asc'
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    }
    return 0;
  });

  // Loading and error states
  if (isLoading) {
    return <div className="text-center py-8">Loading your drafts...</div>;
  }
  if (error) {
    return <div className="text-center py-8 text-red-600">{error}</div>;
  }
  if (!isAuthenticated) {
    return (
      <div className="max-w-md mx-auto mt-10 bg-white p-8 rounded-lg shadow-md text-center">
        <h2 className="text-xl font-semibold mb-4">Login Required</h2>
        <p className="text-gray-600 mb-4">Please login to view your drafts.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <h1 className="text-2xl font-bold mb-6">My Drafts</h1>

      {/* Search and filters */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
         <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-grow">
            <input
              type="text"
              placeholder="Search by name, details, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="date">Sort by Date</option>
              <option value="name">Sort by Name</option>
              <option value="price">Sort by Price</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-2 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
              title={sortOrder === 'asc' ? 'Sort Ascending' : 'Sort Descending'}
            >
              {sortOrder === 'asc' ? '↑ Asc' : '↓ Desc'}
            </button>
          </div>
        </div>
      </div>

      {/* Main layout container */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left side: Drafts table */}
        <div className="flex-grow">
           <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-4 py-3 text-sm text-gray-600 border-b border-gray-200">
          Showing {drafts.length} of {draftsTotal || drafts.length} drafts
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Type</th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Draft Name</th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Details</th>
                <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Price</th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Status</th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Date</th>
                <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-64">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedDrafts.length > 0 ? (
                sortedDrafts.map((draft) => {
                  const draftType = getQuoteType(draft);

                  return (
                    <tr key={draft.id} className="hover:bg-gray-50 align-top">
                      <td className="px-3 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeBadge(draftType)}`}>
                          {draftType}
                        </span>
                      </td>
                      <td className="px-3 py-4 whitespace-normal break-words">
                        <div className="text-sm font-medium text-gray-900">{getDraftName(draft)}</div>
                        <div className="text-xs text-gray-500">ID: {draft.id?.substring(0, 8)}...</div>
                      </td>
                      <td className="px-3 py-4 whitespace-normal break-words">
                        <div className="text-sm text-gray-900">{getDraftSummary(draft)}</div>
                        {getDraftDimensions(draft) && (
                          <div className="text-sm text-gray-500">
                            {getDraftDimensions(draft)} {(draft.items?.[0]?.quantity || draft.quantity) && `× ${draft.items?.[0]?.quantity || draft.quantity}`}
                          </div>
                        )}
                        {((draft.numberOfDesigns > 1) || (draft.items?.[0]?.numberOfDesigns > 1)) && (
                          <div className="text-sm text-gray-500">
                            {(draft.numberOfDesigns || draft.items?.[0]?.numberOfDesigns)} designs
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-medium text-gray-900">
                          £{(draft.totalPrice || draft.price || 0).toFixed(2)}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge('draft')}`}>
                          Draft
                        </span>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(draft.createdAt)}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex flex-wrap gap-1 justify-end">
                          <button
                            title="View Details"
                            className="text-indigo-600 hover:text-indigo-900 transition-colors px-1"
                            onClick={() => viewDraftDetails(draft)}
                          >
                            View
                          </button>

                          <button
                            title="Edit this draft"
                            className="text-green-600 hover:text-green-900 transition-colors px-1"
                            onClick={() => handleEditDraft(draft)}
                          >
                            Edit
                          </button>

                          {currentQuote.items.length > 0 && (
                            <button
                              title="Add to current quote"
                              className="text-purple-600 hover:text-purple-900 transition-colors px-1"
                              onClick={() => handleAddToQuote(draft)}
                            >
                              Add to Quote
                            </button>
                          )}

                          {currentQuote.items.length === 0 && (
                            <button
                              title="Convert to Order"
                              className="text-blue-600 hover:text-blue-900 transition-colors px-1"
                              onClick={() => handleSaveAsQuote(draft)}
                            >
                              Convert to Order
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                    {searchTerm
                      ? 'No drafts match your search criteria.'
                      : 'No drafts found. Create a draft by saving your progress in the calculator.'}
                  </td>
                </tr>
              )}
            </tbody>
            </table>
           </div>
          {draftsHasMore && (
            <div className="mt-4 text-center">
              <button
                onClick={loadMoreDrafts}
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
              >
                {isLoading ? 'Loading...' : 'Load more drafts'}
              </button>
            </div>
          )}
        </div>
        </div>

        {/* Right side: Current Quote Sidebar */}
        {currentQuote.items.length > 0 && (
          <div className="lg:w-80 w-full">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Current Quote
                </h3>
                <span className="text-sm text-gray-500">
                  {currentQuote.items.length} item{currentQuote.items.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Items list */}
              <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                {currentQuote.items.map((item, index) => (
                  <div key={item.id || index} className="flex justify-between items-start py-2 border-b border-gray-100 last:border-b-0">
                    <div className="flex-grow pr-2">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {item.jobName || `Product ${index + 1}`}
                      </p>
                      <p className="text-xs text-gray-500">
                        Qty: {item.quantity || 1}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium text-gray-900 whitespace-nowrap">
                        £{(item.price || 0).toFixed(2)}
                      </div>
                      <button
                        onClick={() => handleRemoveFromQuote(item.id)}
                        className="text-gray-400 hover:text-red-500 transition-colors p-1"
                        title="Remove from quote"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="border-t pt-3 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-base font-semibold text-gray-900">Total:</span>
                  <span className="text-lg font-bold text-gray-900">
                    £{currentQuote.totalPrice.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Action buttons */}
              <div className="space-y-2">
                <button
                  onClick={handleReviewCurrentQuote}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  Review Quote
                </button>
                <button
                  onClick={handleClearCurrentQuote}
                  className="w-full px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                >
                  Clear Quote
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Draft Details Modal */}
      {selectedDraft && (
         <Modal isOpen={!!selectedDraft} onClose={closeDraftDetails} title={`Draft Details: ${getDraftName(selectedDraft)}`} size="4xl">
            <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Draft Information */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium text-lg mb-2">Draft Information</h3>
                      <p><span className="font-medium">Type:</span> {getQuoteType(selectedDraft)}</p>
                      <p><span className="font-medium">Items:</span> {selectedDraft.items?.length || 'In Progress'}</p>
                      <p><span className="font-medium">ID:</span> {selectedDraft.id}</p>
                      <p><span className="font-medium">Created:</span> {formatDate(selectedDraft.createdAt)}</p>
                      <p><span className="font-medium">Last Modified:</span> {formatDate(selectedDraft.updatedAt)}</p>
                    </div>

                    {/* Pricing Information */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium text-lg mb-2">Pricing Details</h3>
                      <div className="text-xl font-bold">
                        <span className="font-medium">Estimated Price: </span>
                        £{(selectedDraft.totalPrice || selectedDraft.price || 0).toFixed(2)}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        *Price may change when converted to quote
                      </p>
                    </div>
                  </div>

                   {/* Products List (if items exist) */}
                  {selectedDraft.items && selectedDraft.items.length > 0 && (
                    <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium text-lg mb-4">Products</h3>
                      <div className="divide-y divide-gray-200 max-h-60 overflow-y-auto">
                        {selectedDraft.items.map((item, index) => (
                          <div key={item.id || index} className="py-3 first:pt-0 last:pb-0">
                             <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium">{item.jobName || `Product ${index + 1}`}</p>
                                <p className="text-sm text-gray-600">
                                  {item.paperStockName || item.paperStock || item.materialNames || item.material || 'N/A'}
                                </p>
                                <p className="text-sm text-gray-600">Size: {item.dimensions || `${item.width}x${item.height}mm`}</p>
                                <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                              </div>
                              <p className="font-medium whitespace-nowrap pl-2">£{item.price?.toFixed(2) || '0.00'}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                   {/* Configuration Details (for in-progress drafts) */}
                  {(!selectedDraft.items || selectedDraft.items.length === 0) && (
                    <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium text-lg mb-4">Configuration</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        {selectedDraft.jobType && (
                          <p><span className="font-medium">Job Type:</span> {selectedDraft.jobType}</p>
                        )}
                        {selectedDraft.paperStock && (
                          <p><span className="font-medium">Paper:</span> {selectedDraft.paperStockName || selectedDraft.paperStock}</p>
                        )}
                        {selectedDraft.material && (
                          <p><span className="font-medium">Material:</span> {selectedDraft.materialNames || selectedDraft.material}</p>
                        )}
                        {(selectedDraft.width && selectedDraft.height) && (
                          <p><span className="font-medium">Dimensions:</span> {selectedDraft.width}x{selectedDraft.height}mm</p>
                        )}
                        {selectedDraft.quantity && (
                          <p><span className="font-medium">Quantity:</span> {selectedDraft.quantity}</p>
                        )}
                        {selectedDraft.numberOfDesigns && selectedDraft.numberOfDesigns > 1 && (
                          <p><span className="font-medium">Designs:</span> {selectedDraft.numberOfDesigns}</p>
                        )}
                        {selectedDraft.finishes && selectedDraft.finishes.length > 0 && (
                          <p><span className="font-medium">Finishes:</span> {Array.isArray(selectedDraft.finishes) ? selectedDraft.finishes.join(', ') : selectedDraft.finishes}</p>
                        )}
                      </div>
                    </div>
                  )}

                   {/* Notes Section */}
                  {selectedDraft.notes && (
                    <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium text-lg mb-2">Notes</h3>
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedDraft.notes}</p>
                    </div>
                  )}
              </div>
              
              {/* Footer with actions */}
              <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
                <button
                  onClick={() => handleEditDraft(selectedDraft)}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Edit Draft
                </button>
                {currentQuote.items.length > 0 && (
                  <button
                    onClick={() => handleAddToQuote(selectedDraft)}
                    className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                  >
                    Add to Quote
                  </button>
                )}
                {currentQuote.items.length === 0 && (
                  <button
                    onClick={() => handleSaveAsQuote(selectedDraft)}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Convert to Order
                  </button>
                )}
                <button
                  onClick={() => handleDeleteDraft(selectedDraft.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Delete
                </button>
                <button
                  onClick={closeDraftDetails}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
         </Modal>
      )}

      {/* Quote Review Modal - Conditional based on user role */}
      {showQuoteReview && (
        hasRole && (hasRole('manager') || hasRole('admin')) ? (
          <ManagerQuoteReview onClose={handleQuoteReviewClose} />
        ) : (
          <QuoteReview onClose={handleQuoteReviewClose} />
        )
      )}
    </div>
  );
};

export default DraftsList;
